// Shared formatting helpers for the Home dashboard.

import { DOW_KOR } from "./constants";

export function maskName(name: string): string {
	if (!name || name.length <= 1) return name;
	return name[0] + "*".repeat(name.length - 1);
}

/** Like maskName but keeps the last character: 오미정 → 오*정 */
export function maskNameKeepLast(name: string): string {
	if (!name || name.length <= 1) return name;
	if (name.length === 2) return name[0] + "*";
	return name[0] + "*".repeat(name.length - 2) + name[name.length - 1];
}

export function maskPhone(phone: string): string {
	if (!phone || phone.length < 7) return phone;
	const parts = phone.split("-");
	if (parts.length === 3) {
		return `${parts[0]}-****-${parts[2]}`;
	}
	if (phone.length >= 8) {
		return `${phone.slice(0, 3)}-****-${phone.slice(-4)}`;
	}
	return phone;
}

/**
 * MRN mask for the patient-list modal (MED.008 confirmed · PRD 02 §3.5.4). Keep the head, hide the last five
 * chars except the final one — prototype `fmt.maskMrn` (예: `M-00012345` → `M-000****5`). Short MRNs (≤4) show
 * as-is. MRN allows letters/digits/dash, max 20 (PRD 11 §6.3).
 */
export function maskMrn(mrn: string): string {
	if (!mrn) return "-";
	if (mrn.length <= 4) return mrn;
	const visible = Math.max(1, mrn.length - 5);
	return mrn.slice(0, visible) + "****" + mrn.slice(-1);
}

/** Birth-date mask for the modal (PRD 02 §3.5.4 · MED.010): year only — `1988-05-12` → `1988-**-**`. */
export function maskBirth(birth: string): string {
	if (!birth) return "-";
	return birth.slice(0, 4) + "-**-**";
}

export function formatDateText(isoStr: string): string {
	const d = new Date(isoStr + "T00:00:00");
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${y}.${m}.${day} (${DOW_KOR[d.getDay()]})`;
}

export function formatSchDate(iso: string): string {
	const d = new Date(iso + "T00:00:00");
	return `${d.getMonth() + 1}/${d.getDate()}(${DOW_KOR[d.getDay()]})`;
}

export function formatNowTime(): string {
	const now = new Date();
	return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
}

/** Format ISO date to YYYY.MM.DD (e.g. 2026-01-10 → 2026.01.10). */
export function formatDate(d: string): string {
	return d.replace(/-/g, ".");
}

/** Age in years from an ISO birth date (e.g. 1979-03-15 → 47). */
export function ageFromBirth(birthDate: string): number {
	if (!birthDate) return 0;
	const b = new Date(birthDate + "T00:00:00");
	const now = new Date();
	let age = now.getFullYear() - b.getFullYear();
	const m = now.getMonth() - b.getMonth();
	if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--;
	return age;
}

/** Short gender token per PRD v1.1: 남→M, 여→F (empty if unknown). */
export function genderAbbr(gender: string): string {
	if (gender === "남" || gender === "M") return "M";
	if (gender === "여" || gender === "F") return "F";
	return "";
}

/** Weeks elapsed since an ISO date string. */
export function weeksSince(dateStr: string): number {
	const then = new Date(dateStr + "T00:00:00");
	const now = new Date();
	return Math.max(
		0,
		Math.floor((now.getTime() - then.getTime()) / (7 * 24 * 60 * 60 * 1000)),
	);
}

/**
 * 0-based PRO treatment-week index for TODAY, anchored to the **Monday of the discharge week**
 * — the same anchor the PRO calendar columns use (ProCalendar `weekBase` / adapter `getWeekStartISO`).
 *
 * `weeksSince` counts from the raw discharge date, which is one week short whenever today falls in a
 * later calendar week but < 7 days after discharge (e.g. discharge Sun, today Thu → still "0 weeks"
 * but already in the W2 column). This anchors to the Monday so the highlighted column always matches
 * the column that actually contains today. Returns -1 before that Monday / when there is no discharge.
 */
export function currentProWeekIndex(dischargeDate: string): number {
	return proWeekIndexAt(dischargeDate);
}

/**
 * The same 0-based Monday-anchored PRO week index as {@link currentProWeekIndex}, but measured to an
 * ARBITRARY reference date instead of today — so a badge can be pinned to a date in the past.
 *
 * `refDate` omitted → today (the {@link currentProWeekIndex} behaviour). Returns -1 when the reference
 * date precedes the anchor Monday, or when either date is absent/unparseable.
 */
export function proWeekIndexAt(
	dischargeDate: string,
	refDate?: string | null,
): number {
	if (!dischargeDate) return -1;
	const monday = new Date(dischargeDate + "T00:00:00");
	if (Number.isNaN(monday.getTime())) return -1;
	const dow = monday.getDay(); // 0=Sun … 6=Sat
	monday.setDate(monday.getDate() + (dow === 0 ? -6 : 1 - dow)); // back to Monday
	let ref: Date;
	if (refDate) {
		ref = new Date(refDate + "T00:00:00");
		if (Number.isNaN(ref.getTime())) return -1;
	} else {
		ref = new Date();
		ref.setHours(0, 0, 0, 0);
	}
	const diff = ref.getTime() - monday.getTime();
	if (diff < 0) return -1;
	return Math.floor(diff / (7 * 24 * 60 * 60 * 1000));
}

/**
 * The week number behind BOTH treatment badges — the patient card's chip and the patient-detail header's
 * (PRD 04 §3.1 `치료기간: [퇴원일~] + [현재(N주 경과)] 또는 [종료일(N주 후 종료, 사유)]`).
 *
 * Anchored on the Monday of the discharge week (the anchor the PRO calendar columns use), and measured:
 * - while the patient is under management → to **today**, so the badge advances each Monday;
 * - once management has ended → to the **종료일**, where it stays put forever. Per the BA's confirmation
 *   (2026-07-29): "sau khi close hoặc withdraw thì thời gian dừng trôi … không update bất cứ gì". The old
 *   code measured an ended patient to today too, so "N주 후 종료" — a value the label states as history —
 *   kept climbing week after week.
 *
 * Returns **null** for an ended patient whose `closeDate` the server has not supplied: the caller drops the
 * number from the badge rather than printing one measured against today (confirmed fallback, 2026-07-30).
 * `closeDate` is cleared by the BE's revert-close flow, so resuming a patient restarts the clock by itself.
 */
export function treatmentWeeks(
	dischargeDate: string,
	ended: boolean,
	closeDate?: string | null,
): number | null {
	if (!ended) return Math.max(0, proWeekIndexAt(dischargeDate));
	if (!closeDate) return null;
	return Math.max(0, proWeekIndexAt(dischargeDate, closeDate));
}

/**
 * The i18n key for a patient's status chip.
 *
 * **판정 축은 `closeReason`이다 — `status`가 아니다.** [MED.018], canonical: 의료진웹 PRD 03 §4.3.
 * 값이 있으면 `관리종료 · 사유`, 없으면 `관리중`. `status`(`closed`/`withdrawn`)는 **접속 허용 여부**를
 * 나타내는 시스템 축이고, 의료진이 보는 **임상 관리 상태**는 `closeReason`이 담당한다.
 *
 * 종전에는 `status`를 먼저 보고 `withdrawn`이면 `탈퇴 · {사유}`를 찍었다. 그래서 두 가지가 깨졌다:
 *   · 관리중에 환자가 앱에서 자발 탈퇴하면 `탈퇴 · 탈퇴` — 어느 축으로도 정의되지 않은 값 (QA 보고).
 *   · **정상만기로 관리종료된 뒤 탈퇴하면 `탈퇴 · 만기`가 되어, 이미 끝난 임상 이력이 '탈퇴'로 덮였다.**
 *     [MED.018]이 이 규칙을 세운 요지가 바로 이 행이다 — 나중의 앱 탈퇴가 임상 결과를 바꾸지 않는다.
 *
 * 어드민은 계정 관리 화면이라 `status`를 직접 노출하지만(어드민 PRD 02 §4.4 `탈퇴` 빨강), 의료진웹은
 * 임상 화면이라 축이 다르다. 그래서 `patients.card.withdrawnReason.*` 라벨군은 의료진웹에서 더 이상
 * 쓰이지 않는다.
 *
 * 공유 헬퍼다 — 환자 카드(PRD 03 §2.3)와 홈 「전체 환자 리스트」(PRD 02 §3.5.4 #12)가 같은 말을 쓰도록
 * 한 곳에서 공급한다([MED.018]이 두 화면을 함께 규정한다).
 */
export function patientStatusLabelKey(
	_status: string,
	closedReason?: string | null,
): string {
	// 사유가 있으면 관리종료, 없으면 관리중 — status는 읽지 않는다.
	return closedReason
		? `medical:patients.card.closeReason.${closedReason}`
		: "medical:patients.card.active";
}

/**
 * Display form of a patient's own mobile: `01012345678` → `010-1234-5678`.
 *
 * Deliberately NOT a masking helper — PRD 04 v2.30 「표시 정보」 puts the patient's raw number in the
 * patient-detail header so a clinician can dial it for the 전화 중재 on the same screen, and a masked
 * number cannot be dialled. Every other surface (환자 목록 PRD 03 · CSV §3.5.7 · 로그) keeps
 * {@link maskPhone}. An already-masked or unexpected shape is returned untouched rather than
 * reformatted, so a value that slipped through a masking layer is never made to look raw.
 */
export function formatPhone(phone: string | null | undefined): string {
	if (!phone) return "";
	const digits = phone.replace(/\D/g, "");
	if (phone.includes("*") || digits.length !== 11) return phone;
	return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}
